<script setup lang="ts">
/**
 * Создание задачи — макет «Создание задачи — панель» на странице «01 · Ядро».
 *
 * Панель, а не окно посреди экрана (DEV-1582). Задачу заводят, глядя на доску:
 * в какую колонку кладут, что уже есть у соседей, какой эпик открыт. Окно этот
 * контекст закрывало собой ровно в тот момент, когда он нужен; панель отъезжает
 * вправо и оставляет доску на виду — той же `AppDrawer`, что и панель задачи.
 *
 * Обязательного здесь ровно одно — название: задачу заводят на ходу, посреди
 * разговора, и форма из семи обязательных полей кончается тем, что её обходят
 * стороной.
 *
 * Валидация без vee-validate и zod, хотя в задаче они названы: правило здесь
 * одно (непустое название до пятисот знаков), рядом уже лежит форма проекта с
 * ручной проверкой, и две библиотеки ради одного правила дали бы два разных
 * подхода к формам в одном приложении. Общее с быстрым созданием в колонке —
 * этот же компонент, а не общая схема: колонка открывает его с проставленной
 * колонкой.
 */
import {
  AppButton,
  AppDatePicker,
  AppDrawer,
  AppSelect,
  AppTextField,
} from '@vinprop/design-system'
import { AppRichTextEditor } from '@vinprop/design-system/editor'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorMessage } from '@/api/errors'
import { setIssueServices } from '@/features/devops/api'
import IssueServicesPicker from '@/features/devops/components/IssueServicesPicker.vue'
import { collectMentionIds, uploadEditorImage, useTrackerEditorExtensions } from '@/features/editor'
import EditorAiActions from '@/features/editor/EditorAiActions.vue'
import {
  listColumns,
  listMembers,
  type ProjectColumn,
  type ProjectMember,
} from '@/features/projects/api'

import {
  createIssue,
  listParentCandidates,
  parentTypesOf,
  typesOfProfile,
  PRIORITIES,
  type CreatedIssue,
  type ParentCandidate,
} from '../api'
import type { V1IssueType, V1Priority } from '@/api/clients/tracker-issue'

const props = defineProps<{
  modelValue: boolean
  projectId: string
  /** Профиль проекта решает, какие типы задач вообще существуют */
  typeProfile: string
  /** Колонка, если создаём из неё; ею же заполняется поле «Статус» */
  columnId?: string
  /** С каким типом открывается форма: на экране эпиков заводят эпик */
  defaultType?: V1IssueType
  /** Готовый заголовок — форму открыли из заметки, а не с чистого листа */
  defaultTitle?: string
  /** Готовое описание: телом задачи ложится тело заметки (DEV-1949) */
  defaultDescription?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  created: [issue: CreatedIssue]
}>()

/** Столько знаков в названии принимает бэкенд */
const TITLE_LIMIT = 500

const { t } = useI18n()
const extensions = useTrackerEditorExtensions(() => props.projectId)

const type = ref<V1IssueType>('ISSUE_TYPE_TASK')
const title = ref('')
const description = ref('')
const priority = ref<V1Priority>('PRIORITY_MEDIUM')
/** Колонка доски = статус задачи; имя другое, чтобы не спорить с одноимённым props */
const statusId = ref('')
const assigneeId = ref('')
const parentId = ref('')
const dueDate = ref('')
const services = ref<string[]>([])
const isSaving = ref(false)
const failure = ref('')

const members = ref<ProjectMember[]>([])
const columns = ref<ProjectColumn[]>([])
const parents = ref<ParentCandidate[]>([])
const isLoadingParents = ref(false)

const types = computed(() =>
  typesOfProfile(props.typeProfile).map((value) => ({
    value,
    label: t(`issues.types.${value}`),
  })),
)

const priorities = computed(() =>
  PRIORITIES.map((value) => ({ value, label: t(`issues.priorities.${value}`) })),
)

/**
 * Статус — та же колонка доски. Раньше его не спрашивали вовсе, и всё, что
 * заводили не из колонки, ложилось в первую открытую: бэклог рос, а задача,
 * которую уже делают, оттуда переносилась руками (DEV-2074).
 */
const statuses = computed(() =>
  columns.value.map((column) => ({ value: column.id, label: column.name })),
)

const assignees = computed(() =>
  members.value
    .filter((member) => member.name)
    .map((member) => ({ value: member.id, label: member.name })),
)

const parentOptions = computed(() =>
  parents.value.map((issue) => ({ value: issue.id, label: `${issue.key} · ${issue.title}` })),
)

/** У эпика родителя не бывает — поля тоже */
const hasParent = computed(() => parentTypesOf(type.value).length > 0)

/**
 * Поле показываем, только когда есть из чего выбрать: в простом проекте у
 * задачи родителем был бы эпик, а эпиков там не бывает вовсе — пустой список
 * «Родитель» в такой форме только сбивает.
 */
const showParent = computed(
  () => hasParent.value && (isLoadingParents.value || parents.value.length > 0),
)

const titleError = computed(() =>
  title.value.length > TITLE_LIMIT ? t('issues.create.errors.title', { limit: TITLE_LIMIT }) : '',
)

const canSubmit = computed(
  () => title.value.trim().length > 0 && !titleError.value && !isSaving.value,
)

const reset = () => {
  type.value = props.defaultType ?? 'ISSUE_TYPE_TASK'
  title.value = props.defaultTitle ?? ''
  description.value = props.defaultDescription ?? ''
  priority.value = 'PRIORITY_MEDIUM'
  statusId.value = props.columnId ?? ''
  assigneeId.value = ''
  parentId.value = ''
  dueDate.value = ''
  services.value = []
  failure.value = ''
}

/**
 * Кандидаты в родители зависят от типа: у подзадачи это задачи и баги, у
 * задачи — эпики. Молча пропускаем отказ: без списка родителя просто не
 * выбрать, а форма нужна и так.
 */
const loadParents = async () => {
  const wanted = parentTypesOf(type.value)

  parentId.value = ''
  parents.value = []

  if (!wanted.length || !props.projectId) return

  isLoadingParents.value = true

  try {
    parents.value = await listParentCandidates(props.projectId, wanted)
  } catch {
    parents.value = []
  } finally {
    isLoadingParents.value = false
  }
}

// Форму чистим на открытии, а не на закрытии: закрыли по ошибке — вернулись и
// дописали, а не набрали заново. `immediate` — на случай, когда компонент
// создают уже открытым: без него форма осталась бы без исполнителей и
// родителей.
watch(
  () => props.modelValue,
  async (open) => {
    if (!open) return

    reset()

    // Имена участников нужны исполнителю, колонки — статусу; отказ здесь не
    // повод не дать завести задачу — останется поле без вариантов.
    const [people, board] = await Promise.all([
      listMembers(props.projectId).catch(() => []),
      listColumns(props.projectId).catch(() => []),
    ])

    members.value = people
    columns.value = board
    // Из колонки открыли — её и предлагаем; иначе первую на доске, ровно ту,
    // куда задача легла бы и без выбора.
    statusId.value = props.columnId || board[0]?.id || ''

    await loadParents()
  },
  { immediate: true },
)

watch(type, loadParents)

const close = () => emit('update:modelValue', false)

const submit = async () => {
  if (!canSubmit.value) return

  isSaving.value = true
  failure.value = ''

  try {
    const issue = await createIssue({
      projectId: props.projectId,
      title: title.value.trim(),
      descriptionMd: description.value,
      type: type.value,
      priority: priority.value,
      columnId: statusId.value,
      parentId: hasParent.value ? parentId.value : '',
      assigneeId: assigneeId.value,
      dueDate: dueDate.value,
      mentionedUserIds: collectMentionIds(description.value),
    })

    // Состав задаётся отдельной ручкой: задачи до её создания ещё нет, а
    // связать сервисы с ней можно только по идентификатору. Отказ здесь не
    // отменяет созданную задачу — сервисы допишут в карточке.
    if (services.value.length) {
      await setIssueServices(issue.id, services.value).catch(() => undefined)
    }

    emit('created', issue)
    close()
  } catch (error) {
    // Ошибку показываем в форме и её не закрываем: набранное описание дороже
    // аккуратного закрытия окна.
    failure.value = errorMessage(error, t('issues.create.errors.failed'))
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <AppDrawer
    :model-value="modelValue"
    :title="t('issues.create.title')"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <form class="create-issue" @submit.prevent="submit">
      <div class="create-issue__row">
        <AppSelect
          v-model="type"
          class="create-issue__type"
          :options="types"
          :label="t('issues.create.type')"
          size="small"
        />
        <AppTextField
          v-model="title"
          class="create-issue__title"
          :label="t('issues.create.name')"
          :placeholder="t('issues.create.namePlaceholder')"
          :error="titleError"
          required
        />
      </div>

      <div class="create-issue__field">
        <span class="create-issue__label">{{ t('issues.create.description') }}</span>
        <!-- Потолок держит форму целиком в панели: без него растущее описание
             уводило в прокрутку и кнопки сохранения — за длинным текстом их
             приходилось искать. Прокручивается сам текст. -->
        <AppRichTextEditor
          v-model="description"
          preset="issue"
          max-height="clamp(180px, 40vh, 460px)"
          :extensions="extensions"
          :placeholder="t('issues.create.descriptionPlaceholder')"
          :upload-image="uploadEditorImage"
        >
          <!-- Задачи ещё нет, поэтому гейт спрашивает про проект: содержимое
               уходит в модель до всякого сохранения (DEV-1578). -->
          <template #bubble-extra="{ editor }">
            <EditorAiActions v-model:value="description" :editor="editor" :project="projectId" />
          </template>
        </AppRichTextEditor>
      </div>

      <div class="create-issue__row">
        <AppSelect
          v-model="priority"
          class="create-issue__half"
          :options="priorities"
          :label="t('issues.create.priority')"
          size="small"
        />
        <AppSelect
          v-if="statuses.length"
          v-model="statusId"
          class="create-issue__half"
          :options="statuses"
          :label="t('issues.create.status')"
          size="small"
        />
        <AppSelect
          v-model="assigneeId"
          class="create-issue__half"
          :options="assignees"
          :label="t('issues.create.assignee')"
          :placeholder="t('issues.create.assigneePlaceholder')"
          :empty-text="t('issues.create.noMembers')"
          size="small"
          searchable
          clearable
        />
      </div>

      <div class="create-issue__row">
        <AppSelect
          v-if="showParent"
          v-model="parentId"
          class="create-issue__half"
          :options="parentOptions"
          :label="t('issues.create.parent')"
          :placeholder="t('issues.create.parentPlaceholder')"
          :empty-text="t('issues.create.noParents')"
          :loading="isLoadingParents"
          size="small"
          searchable
          clearable
        />
        <div class="create-issue__half create-issue__field">
          <span class="create-issue__label">{{ t('issues.create.dueDate') }}</span>
          <AppDatePicker v-model="dueDate" />
        </div>
      </div>

      <!-- Сервисы задачи: из них соберётся её фича-стенд. Спрашиваем при
           постановке — на этот момент веток ещё нет, а знание о том, что
           фича трогает фронт, уже есть (DEV-1626). -->
      <div class="create-issue__field">
        <span class="create-issue__label">{{ t('issue.meta.services') }}</span>
        <IssueServicesPicker v-model="services" />
      </div>

      <p v-if="failure" class="create-issue__error">{{ failure }}</p>
    </form>

    <template #footer>
      <AppButton variant="secondary" @click="close">{{ t('common.actions.cancel') }}</AppButton>
      <AppButton variant="primary" :disabled="!canSubmit" :loading="isSaving" @click="submit">
        {{ t('issues.create.submit') }}
      </AppButton>
    </template>
  </AppDrawer>
</template>

<style scoped>
.create-issue {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.create-issue__row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

/* Тип короткий и не растягивается: в нём пять слов на выбор. */
.create-issue__type {
  width: 160px;
  flex-shrink: 0;
}

/* Панель уже окна (560 против 720): пары полей в ней встают тесно, и на узком
   экране, где панель занимает его целиком, — тем более. Ниже 600 ряды
   разворачиваются в столбец. */
@media (max-width: 600px) {
  .create-issue__row {
    flex-direction: column;
  }

  .create-issue__type {
    width: 100%;
  }
}

.create-issue__title {
  flex: 1;
  min-width: 0;
}

.create-issue__half {
  flex: 1;
  min-width: 0;
}

.create-issue__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.create-issue__label {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
}

.create-issue__error {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--font-size-body-sm);
}
</style>
